// ProductsOrderForm.js - Product order form with multiple product selection
import { DEVICES } from '../../../constants/breakpoints';

export const createProductsOrderFormElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const sectionClass = 'products-order-section';
  const containerClass = 'products-order-container';
  const headerClass = 'products-order-header';
  const titleClass = 'products-order-title';
  const subtitleClass = 'products-order-subtitle';
  const contentClass = 'products-order-content';
  const productsGridClass = 'products-order-grid';
  const productCardClass = 'products-order-card';
  const productImageClass = 'products-order-image';
  const productInfoClass = 'products-order-info';
  const productTitleClass = 'products-order-product-title';
  const productPriceClass = 'products-order-price';
  const productCheckboxClass = 'products-order-checkbox';
  const formSectionClass = 'products-order-form-section';
  const formClass = 'products-order-form';
  const formGroupClass = 'products-order-form-group';
  const formLabelClass = 'products-order-form-label';
  const formInputClass = 'products-order-form-input';
  const formSelectClass = 'products-order-form-select';
  const formButtonClass = 'products-order-form-button';
  const summaryClass = 'products-order-summary';
  const successMessageClass = 'products-order-success';
  const errorMessageClass = 'products-order-error';

  // Desktop Styles - Herbal theme with natural green colors
  const sectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#f0fdf4',
    background: `
      radial-gradient(circle at 20% 30%, rgba(34, 139, 34, 0.08) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(144, 238, 144, 0.06) 0%, transparent 60%),
      linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)
    `,
    color: '#1a3d1a',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    zIndex: '2'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const titleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: '1.1'
  };

  const subtitleStyles = {
    fontSize: '20px',
    color: '#4a5568',
    lineHeight: '1.6',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: '400',
    fontFamily: '"Inter", sans-serif'
  };

  const contentStyles = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '60px',
    alignItems: 'start'
  };

  const productsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '40px'
  };

  const productCardStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '2px solid rgba(144, 238, 144, 0.2)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    position: 'relative',
    boxShadow: '0 4px 15px rgba(34, 139, 34, 0.1)'
  };

  const productImageStyles = {
    width: '100%',
    height: '200px',
    objectFit: 'cover'
  };

  const productInfoStyles = {
    padding: '24px'
  };

  const productTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1a3d1a',
    marginBottom: '12px',
    fontFamily: '"Inter", sans-serif'
  };

  const productPriceStyles = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#228b22',
    fontFamily: '"Inter", sans-serif'
  };

  const productCheckboxStyles = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '24px',
    height: '24px',
    accentColor: '#228b22'
  };

  const formSectionStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '40px',
    border: '2px solid rgba(144, 238, 144, 0.2)',
    boxShadow: '0 10px 25px -5px rgba(34, 139, 34, 0.1), 0 10px 10px -5px rgba(34, 139, 34, 0.04)',
    position: 'sticky',
    top: '120px'
  };

  const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const formLabelStyles = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1a3d1a',
    fontFamily: '"Inter", sans-serif'
  };

  const formInputStyles = {
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    border: '2px solid rgba(144, 238, 144, 0.3)',
    borderRadius: '12px',
    color: '#1a3d1a',
    fontSize: '16px',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formButtonStyles = {
    padding: '18px 40px',
    backgroundColor: '#228b22',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginTop: '20px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const summaryStyles = {
    backgroundColor: '#f0fdf4',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid rgba(144, 238, 144, 0.2)'
  };

  const successMessageStyles = {
    padding: '15px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    borderRadius: '12px',
    marginTop: '20px',
    display: 'none',
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    border: '1px solid rgba(34, 197, 94, 0.2)'
  };

  const errorMessageStyles = {
    padding: '15px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '12px',
    marginTop: '20px',
    display: 'none',
    fontFamily: '"Inter", sans-serif',
    fontSize: '14px',
    border: '1px solid rgba(239, 68, 68, 0.2)'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    newRegistry[DEVICES.DESKTOP].set(sectionClass, sectionStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(headerClass, headerStyles);
    newRegistry[DEVICES.DESKTOP].set(titleClass, titleStyles);
    newRegistry[DEVICES.DESKTOP].set(subtitleClass, subtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contentClass, contentStyles);
    newRegistry[DEVICES.DESKTOP].set(productsGridClass, productsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(productCardClass, productCardStyles);
    newRegistry[DEVICES.DESKTOP].set(productImageClass, productImageStyles);
    newRegistry[DEVICES.DESKTOP].set(productInfoClass, productInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(productTitleClass, productTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(productPriceClass, productPriceStyles);
    newRegistry[DEVICES.DESKTOP].set(productCheckboxClass, productCheckboxStyles);
    newRegistry[DEVICES.DESKTOP].set(formSectionClass, formSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(formClass, formStyles);
    newRegistry[DEVICES.DESKTOP].set(formGroupClass, formGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(formLabelClass, formLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(formInputClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formSelectClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formButtonClass, formButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(summaryClass, summaryStyles);
    newRegistry[DEVICES.DESKTOP].set(successMessageClass, successMessageStyles);
    newRegistry[DEVICES.DESKTOP].set(errorMessageClass, errorMessageStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contentClass, { gridTemplateColumns: '1fr', gap: '40px' });
    newRegistry[DEVICES.TABLET].set(titleClass, { fontSize: '48px' });
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(sectionClass, { paddingTop: '80px', paddingBottom: '80px', paddingLeft: '20px', paddingRight: '20px' });
    newRegistry[DEVICES.MOBILE].set(titleClass, { fontSize: '36px' });
    newRegistry[DEVICES.MOBILE].set(formSectionClass, { padding: '24px' });
    
    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    sectionClass, containerClass, headerClass, titleClass, subtitleClass,
    contentClass, productsGridClass, productCardClass, productImageClass,
    productInfoClass, productTitleClass, productPriceClass, productCheckboxClass,
    formSectionClass, formClass, formGroupClass, formLabelClass,
    formInputClass, formSelectClass, formButtonClass, summaryClass,
    successMessageClass, errorMessageClass
  ]));

  // Generate IDs
  const baseId = Date.now();
  const sectionId = baseId;
  const containerId = baseId + 1;
  const headerId = baseId + 2;
  const titleId = baseId + 3;
  const subtitleId = baseId + 4;
  const contentId = baseId + 5;
  const productsGridId = baseId + 6;
  const formSectionId = baseId + 7;

  // Product card IDs
  const product1Id = baseId + 10;
  const product1CheckboxId = baseId + 11;
  const product1ImageId = baseId + 12;
  const product1InfoId = baseId + 13;
  const product1TitleId = baseId + 14;
  const product1PriceId = baseId + 15;

  const product2Id = baseId + 16;
  const product2CheckboxId = baseId + 17;
  const product2ImageId = baseId + 18;
  const product2InfoId = baseId + 19;
  const product2TitleId = baseId + 20;
  const product2PriceId = baseId + 21;

  const product3Id = baseId + 22;
  const product3CheckboxId = baseId + 23;
  const product3ImageId = baseId + 24;
  const product3InfoId = baseId + 25;
  const product3TitleId = baseId + 26;
  const product3PriceId = baseId + 27;

  // Form field IDs
  const formId = baseId + 40;
  const summaryId = baseId + 41;
  const summaryTitleId = baseId + 42;
  const selectedProductsId = baseId + 43;
  const totalContainerId = baseId + 44;
  const totalLabelId = baseId + 45;
  const totalPriceId = baseId + 46;
  const nameGroupId = baseId + 47;
  const nameLabelId = baseId + 48;
  const nameInputId = baseId + 49;
  const emailGroupId = baseId + 50;
  const emailLabelId = baseId + 51;
  const emailInputId = baseId + 52;
  const phoneGroupId = baseId + 53;
  const phoneLabelId = baseId + 54;
  const phoneInputId = baseId + 55;
  const addressGroupId = baseId + 56;
  const addressLabelId = baseId + 57;
  const addressTextareaId = baseId + 58;
  const hiddenProductsId = baseId + 62;
  const hiddenTotalId = baseId + 63;
  const submitButtonId = baseId + 64;
  const successMessageId = baseId + 65;
  const errorMessageId = baseId + 66;

  const elements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Products Order Section',
      parentId: null,
      className: sectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: { 'data-element': 'products-order-section' }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: { 'data-element': 'order-container' }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: headerClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: { 'data-element': 'order-header' }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Order Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h1'),
      className: titleClass,
      styles: {},
      children: [],
      text: 'Order Your Natural Products',
      attributes: { 'data-element': 'order-title' }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Order Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: subtitleClass,
      styles: {},
      children: [],
      text: 'Select your favorite herbal products and complete your order for natural skincare goodness.',
      attributes: { 'data-element': 'order-subtitle' }
    },

    // Content Grid
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contentClass,
      styles: {},
      children: [productsGridId, formSectionId],
      text: '',
      attributes: { 'data-element': 'order-content' }
    },

    // Products Grid Container
    {
      id: productsGridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Products Grid',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: productsGridClass,
      styles: {},
      children: [product1Id, product2Id, product3Id],
      text: '',
      attributes: { 'data-element': 'products-grid' }
    },

    // Product 1 - Herbal Soap
    {
      id: product1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Product',
      parentId: productsGridId,
      hierarchyName: getHierarchyName('div'),
      className: productCardClass,
      styles: {},
      children: [product1CheckboxId, product1ImageId, product1InfoId],
      text: '',
      attributes: { 'data-element': 'product-card', 'data-product': 'herbal-soap' }
    },

    // Product 1 Checkbox
    {
      id: product1CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Herbal Soap Checkbox',
      parentId: product1Id,
      hierarchyName: getHierarchyName('input'),
      className: productCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'products',
        'value': 'herbal-soap',
        'data-price': '12.99',
        'data-element': 'product-checkbox'
      }
    },

    // Product 1 Image
    {
      id: product1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Herbal Soap Image',
      parentId: product1Id,
      hierarchyName: getHierarchyName('image'),
      className: productImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Herbal Soap',
        'data-element': 'product-image'
      }
    },

    // Product 1 Info
    {
      id: product1InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Info',
      parentId: product1Id,
      hierarchyName: getHierarchyName('div'),
      className: productInfoClass,
      styles: {},
      children: [product1TitleId, product1PriceId],
      text: '',
      attributes: { 'data-element': 'product-info' }
    },

    // Product 1 Title
    {
      id: product1TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Herbal Soap Title',
      parentId: product1InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: productTitleClass,
      styles: {},
      children: [],
      text: 'Natural Herbal Soap',
      attributes: { 'data-element': 'product-title' }
    },

    // Product 1 Price
    {
      id: product1PriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Soap Price',
      parentId: product1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: productPriceClass,
      styles: {},
      children: [],
      text: '$12.99',
      attributes: { 'data-element': 'product-price' }
    },

    // Product 2 - Ubtan
    {
      id: product2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ubtan Product',
      parentId: productsGridId,
      hierarchyName: getHierarchyName('div'),
      className: productCardClass,
      styles: {},
      children: [product2CheckboxId, product2ImageId, product2InfoId],
      text: '',
      attributes: { 'data-element': 'product-card', 'data-product': 'ubtan' }
    },

    // Product 2 Checkbox
    {
      id: product2CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Ubtan Checkbox',
      parentId: product2Id,
      hierarchyName: getHierarchyName('input'),
      className: productCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'products',
        'value': 'ubtan',
        'data-price': '18.99',
        'data-element': 'product-checkbox'
      }
    },

    // Product 2 Image
    {
      id: product2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Ubtan Image',
      parentId: product2Id,
      hierarchyName: getHierarchyName('image'),
      className: productImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?q=80&w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Traditional Ubtan',
        'data-element': 'product-image'
      }
    },

    // Product 2 Info
    {
      id: product2InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ubtan Info',
      parentId: product2Id,
      hierarchyName: getHierarchyName('div'),
      className: productInfoClass,
      styles: {},
      children: [product2TitleId, product2PriceId],
      text: '',
      attributes: { 'data-element': 'product-info' }
    },

    // Product 2 Title
    {
      id: product2TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Ubtan Title',
      parentId: product2InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: productTitleClass,
      styles: {},
      children: [],
      text: 'Traditional Herbal Ubtan',
      attributes: { 'data-element': 'product-title' }
    },

    // Product 2 Price
    {
      id: product2PriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Ubtan Price',
      parentId: product2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: productPriceClass,
      styles: {},
      children: [],
      text: '$18.99',
      attributes: { 'data-element': 'product-price' }
    },

    // Product 3 - Herbal Facewash
    {
      id: product3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Facewash Product',
      parentId: productsGridId,
      hierarchyName: getHierarchyName('div'),
      className: productCardClass,
      styles: {},
      children: [product3CheckboxId, product3ImageId, product3InfoId],
      text: '',
      attributes: { 'data-element': 'product-card', 'data-product': 'herbal-facewash' }
    },

    // Product 3 Checkbox
    {
      id: product3CheckboxId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Herbal Facewash Checkbox',
      parentId: product3Id,
      hierarchyName: getHierarchyName('input'),
      className: productCheckboxClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'checkbox',
        'name': 'products',
        'value': 'herbal-facewash',
        'data-price': '22.99',
        'data-element': 'product-checkbox'
      }
    },

    // Product 3 Image
    {
      id: product3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Herbal Facewash Image',
      parentId: product3Id,
      hierarchyName: getHierarchyName('image'),
      className: productImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=400&h=200&fit=crop',
      children: [],
      attributes: { 
        'alt': 'Herbal Facewash',
        'data-element': 'product-image'
      }
    },

    // Product 3 Info
    {
      id: product3InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Facewash Info',
      parentId: product3Id,
      hierarchyName: getHierarchyName('div'),
      className: productInfoClass,
      styles: {},
      children: [product3TitleId, product3PriceId],
      text: '',
      attributes: { 'data-element': 'product-info' }
    },

    // Product 3 Title
    {
      id: product3TitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Herbal Facewash Title',
      parentId: product3InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: productTitleClass,
      styles: {},
      children: [],
      text: 'Organic Herbal Face Wash',
      attributes: { 'data-element': 'product-title' }
    },

    // Product 3 Price
    {
      id: product3PriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Herbal Facewash Price',
      parentId: product3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: productPriceClass,
      styles: {},
      children: [],
      text: '$22.99',
      attributes: { 'data-element': 'product-price' }
    },

    // Form Section
    {
      id: formSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Form Section',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: formSectionClass,
      styles: {},
      children: [summaryId, formId, successMessageId, errorMessageId],
      text: '',
      attributes: { 'data-element': 'order-form-section' }
    },

    // Summary
    {
      id: summaryId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Order Summary',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: summaryClass,
      styles: {},
      children: [summaryTitleId, selectedProductsId, totalContainerId],
      text: '',
      attributes: { 'data-element': 'order-summary' }
    },

    // Summary Title
    {
      id: summaryTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Summary Title',
      parentId: summaryId,
      hierarchyName: getHierarchyName('h3'),
      styles: { margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1a3d1a' },
      children: [],
      text: 'Selected Products',
      attributes: { 'data-element': 'summary-title' }
    },

    // Selected Products Display
    {
      id: selectedProductsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Selected Products',
      parentId: summaryId,
      hierarchyName: getHierarchyName('div'),
      styles: { marginBottom: '16px', color: '#4a5568' },
      children: [],
      text: 'No products selected',
      attributes: { 'id': 'selected-products', 'data-element': 'selected-products' }
    },

    // Total Container
    {
      id: totalContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Total Container',
      parentId: summaryId,
      hierarchyName: getHierarchyName('div'),
      styles: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: '600', color: '#1a3d1a' },
      children: [totalLabelId, totalPriceId],
      text: '',
      attributes: { 'data-element': 'total-container' }
    },

    // Total Label
    {
      id: totalLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Total Label',
      parentId: totalContainerId,
      hierarchyName: getHierarchyName('span'),
      styles: {},
      children: [],
      text: 'Total:',
      attributes: { 'data-element': 'total-label' }
    },

    // Total Price
    {
      id: totalPriceId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Total Price',
      parentId: totalContainerId,
      hierarchyName: getHierarchyName('span'),
      styles: { fontSize: '20px', color: '#228b22' },
      children: [],
      text: '$0.00',
      attributes: { 'id': 'total-price', 'data-element': 'total-price' }
    },

    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Order Form',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('form'),
      className: formClass,
      styles: {},
      children: [nameGroupId, emailGroupId, phoneGroupId, addressGroupId, hiddenProductsId, hiddenTotalId, submitButtonId],
      text: '',
      attributes: { 'data-element': 'order-form', 'method': 'post' }
    },

    // Name Field
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Full Name *',
      attributes: { 'for': 'customer-name', 'data-element': 'form-label' }
    },

    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'text',
        'id': 'customer-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Email Field
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Email Address *',
      attributes: { 'for': 'customer-email', 'data-element': 'form-label' }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'email',
        'id': 'customer-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Phone Field
    {
      id: phoneGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [phoneLabelId, phoneInputId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Phone Label
    {
      id: phoneLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Phone Label',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Phone Number *',
      attributes: { 'for': 'customer-phone', 'data-element': 'form-label' }
    },

    // Phone Input
    {
      id: phoneInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Phone Input',
      parentId: phoneGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'tel',
        'id': 'customer-phone',
        'name': 'phone',
        'placeholder': 'Enter your phone number',
        'required': true,
        'data-element': 'form-input'
      }
    },

    // Address Field
    {
      id: addressGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [addressLabelId, addressTextareaId],
      text: '',
      attributes: { 'data-element': 'form-group' }
    },

    // Address Label
    {
      id: addressLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Address Label',
      parentId: addressGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Delivery Address *',
      attributes: { 'for': 'delivery-address', 'data-element': 'form-label' }
    },

    // Address Textarea
    {
      id: addressTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Address Textarea',
      parentId: addressGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: formInputClass,
      styles: { minHeight: '80px', resize: 'vertical' },
      children: [],
      text: '',
      attributes: { 
        'id': 'delivery-address',
        'name': 'address',
        'placeholder': 'Enter your complete delivery address...',
        'rows': '3',
        'required': true,
        'data-element': 'form-textarea'
      }
    },

    // Hidden field for selected products
    {
      id: hiddenProductsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Hidden Products Field',
      parentId: formId,
      hierarchyName: getHierarchyName('input'),
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'hidden',
        'id': 'selected-products-data',
        'name': 'selectedProducts',
        'value': '',
        'data-element': 'hidden-products'
      }
    },

    // Hidden field for total price
    {
      id: hiddenTotalId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Hidden Total Field',
      parentId: formId,
      hierarchyName: getHierarchyName('input'),
      styles: {},
      children: [],
      text: '',
      attributes: { 
        'type': 'hidden',
        'id': 'total-price-data',
        'name': 'totalPrice',
        'value': '0',
        'data-element': 'hidden-total'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: formButtonClass,
      styles: {},
      children: [],
      text: 'Place Order',
      attributes: { 'data-element': 'form-button', 'type': 'submit' },
    },

    // Success Message
    {
      id: successMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Success Message',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: successMessageClass,
      styles: {},
      children: [],
      text: 'Order placed successfully! You will receive a confirmation email shortly.',
      attributes: { 'data-element': 'success-message', 'id': 'order-success' }
    },

    // Error Message
    {
      id: errorMessageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Error Message',
      parentId: formSectionId,
      hierarchyName: getHierarchyName('div'),
      className: errorMessageClass,
      styles: {},
      children: [],
      text: 'There was an error placing your order. Please try again.',
      attributes: { 'data-element': 'error-message', 'id': 'order-error' }
    }
  ];

  // Add product selection functionality
  const productSelectionScript = `
    function initProductSelection() {
      console.log('Product selection script loaded');
      
      const productCards = document.querySelectorAll('[data-product]');
      
      // Try multiple ways to find the summary elements
      let selectedProductsDiv = document.getElementById('selected-products');
      let totalPriceSpan = document.getElementById('total-price');
      
      // Fallback: try finding by data attributes
      if (!selectedProductsDiv) {
        selectedProductsDiv = document.querySelector('[data-element="selected-products"]');
      }
      if (!totalPriceSpan) {
        totalPriceSpan = document.querySelector('[data-element="total-price"]');
      }
      
      console.log('Found product cards:', productCards.length);
      console.log('Selected products div:', selectedProductsDiv);
      console.log('Total price span:', totalPriceSpan);
      
      function updateSummary() {
        console.log('updateSummary called');
        
        const selectedCheckboxes = document.querySelectorAll('input[name="products"]:checked');
        console.log('Selected checkboxes:', selectedCheckboxes.length);
        
        let total = 0;
        let productNames = [];
        
        selectedCheckboxes.forEach(checkbox => {
          console.log('Processing checkbox:', checkbox.value, checkbox.dataset.price);
          total += parseFloat(checkbox.dataset.price);
          productNames.push(checkbox.value.replace('-', ' ').replace(/\\b\\w/g, l => l.toUpperCase()));
        });
        
        console.log('Product names:', productNames);
        console.log('Total:', total);
        
        // Find elements again each time
        let currentSelectedProductsDiv = document.getElementById('selected-products') || document.querySelector('[data-element="selected-products"]');
        let currentTotalPriceSpan = document.getElementById('total-price') || document.querySelector('[data-element="total-price"]');
        
        console.log('Current selected products div:', currentSelectedProductsDiv);
        console.log('Current total price span:', currentTotalPriceSpan);
        
        if (currentSelectedProductsDiv) {
          if (productNames.length === 0) {
            currentSelectedProductsDiv.textContent = 'No products selected';
          } else {
            // Clear existing content
            currentSelectedProductsDiv.textContent = '';
            
            // Add each product as a separate text node
            productNames.forEach((name, index) => {
              const productItem = document.createElement('div');
              productItem.style.marginBottom = '4px';
              productItem.textContent = '• ' + name;
              currentSelectedProductsDiv.appendChild(productItem);
            });
          }
        } else {
          console.error('selected-products element not found!');
        }
        
        if (currentTotalPriceSpan) {
          currentTotalPriceSpan.textContent = '$' + total.toFixed(2);
        } else {
          console.error('total-price element not found!');
        }
        
        // Update hidden form fields
        const hiddenProductsField = document.getElementById('selected-products-data');
        const hiddenTotalField = document.getElementById('total-price-data');
        
        if (hiddenProductsField) {
          // Store selected products as JSON string
          const selectedProductsData = selectedCheckboxes.length > 0 ? 
            Array.from(selectedCheckboxes).map(cb => ({
              name: cb.value,
              price: parseFloat(cb.dataset.price),
              title: cb.value.replace('-', ' ').replace(/\\b\\w/g, l => l.toUpperCase())
            })) : [];
          
          hiddenProductsField.value = JSON.stringify(selectedProductsData);
          console.log('Updated hidden products field:', hiddenProductsField.value);
        } else {
          console.error('Hidden products field not found!');
        }
        
        if (hiddenTotalField) {
          hiddenTotalField.value = total.toFixed(2);
          console.log('Updated hidden total field:', hiddenTotalField.value);
        } else {
          console.error('Hidden total field not found!');
        }
      }
      
      productCards.forEach(card => {
        const checkbox = card.querySelector('input[type="checkbox"]');
        console.log('Processing card:', card, 'checkbox:', checkbox);
        
        if (checkbox) {
          card.addEventListener('click', function(e) {
            console.log('Card clicked:', e.target.type);
            if (e.target.type !== 'checkbox') {
              checkbox.checked = !checkbox.checked;
              console.log('Checkbox toggled to:', checkbox.checked);
              updateSummary();
            }
          });
          
          checkbox.addEventListener('change', function() {
            console.log('Checkbox changed:', this.checked);
            updateSummary();
            if (this.checked) {
              card.style.borderColor = '#228b22';
              card.style.boxShadow = '0 0 0 3px rgba(34, 139, 34, 0.1)';
            } else {
              card.style.borderColor = 'rgba(144, 238, 144, 0.2)';
              card.style.boxShadow = '0 4px 15px rgba(34, 139, 34, 0.1)';
            }
          });
        }
      });
      
      // Initial update
      console.log('Running initial update');
      updateSummary();
    }
    
    // Try multiple initialization strategies
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initProductSelection);
    } else {
      // DOM is already loaded, try immediately
      initProductSelection();
    }
    
    // Also try with a small delay as fallback
    setTimeout(initProductSelection, 100);
    setTimeout(initProductSelection, 500);
  `;

  // Add the script to the first element
//   elements[0].attributes['data-script'] = productSelectionScript;

  return {
    orderSection: elements[0],
    elements: elements
  };
}; 